import { useEffect, useState } from 'react';
import { initKeycloak, getValidToken, isAuthlessPath, isBffMode, isSessionAlive } from '@/features/auth';
import { ensureAiHealth } from '@/features/ai-assistant';

export type BootStepId = 'theme' | 'auth';
export type BootStepState = 'pending' | 'active' | 'done' | 'fail' | 'skip';
export interface BootStep {
  id: BootStepId;
  label: string;
  state: BootStepState;
}

const BOOT_TIMEOUT_MS = 15_000;

const LAST_STEP_LINGER_MS = 300;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const INITIAL: BootStep[] = [
  { id: 'theme', label: 'Наводим порядок', state: 'done' },
  { id: 'auth', label: 'Устанавливаем соединение', state: 'active' },
];

export function useAppBoot() {
  const [steps, setSteps] = useState<BootStep[]>(INITIAL);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let stop = false;
    let onScreen = true;
    const set = (id: BootStepId, state: BootStepState) => {
      if (!onScreen) return;
      setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, state } : s)));
    };

    const watchdog = setTimeout(() => {
      if (stop) return;
      set('auth', 'fail');
      onScreen = false;
      setReady(true);
    }, BOOT_TIMEOUT_MS);

    const auth = isAuthlessPath() ? Promise.resolve() : initKeycloak();

    const authStep = auth
      .then(() => !stop && set('auth', 'done'))
      .catch(() => !stop && set('auth', 'fail'));

    void auth
      .then(() => getValidToken())
      .then((token) => {
        if (token || (isBffMode() && isSessionAlive())) void ensureAiHealth();
      })
      .catch(() => undefined);

    authStep
      .then(() => wait(LAST_STEP_LINGER_MS))
      .then(() => {
        clearTimeout(watchdog);
        if (stop) return;
        onScreen = false;
        setReady(true);
      });

    return () => {
      stop = true;
      clearTimeout(watchdog);
    };
  }, []);

  return { steps, ready };
}
